import { Fragment, useState, useEffect, useRef } from 'react';
import { readOrgFile, writeOrgFile, listOrgCollection } from '../../storage/FileSystemAdapter';
import { appendControlChangeLogs } from '../../utils/changeLog';
import SOA_CONTROLS from '../../data/soa-controls';
import PeopleSelector from '../shared/PeopleSelector';
import EvidenceSelector from '../shared/EvidenceSelector';
import ConfirmDialog from '../shared/ConfirmDialog';
import ImportWizard from '../shared/ImportWizard';
import { BTN_SECONDARY, BG_SUBTLE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, STATUS_GOOD, STATUS_INFO, BG_SHELL, STATUS_WARN, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_LIST_ITEM, FONT_STAT_VALUE } from '../../theme';

const aId = (id) => id ? `A.${id}` : id;

// ── IMPLEMENTATION STATUS COLORS (match risk badge palette) ───────────────────
const STATUS_COLORS = {
  'Not implemented':       { bg: ERROR_BG, text: ERROR_TEXT },
  'Partially implemented': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Implemented':           { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

// ── CATEGORY LABELS (i18n) ────────────────────────────────────────────────────
const CATEGORY_LABELS = {
  '5-Organizational': { es: 'Controles organizacionales', en: 'Organizational controls' },
  '6-People':         { es: 'Controles de personas',      en: 'People controls' },
  '7-Physical':       { es: 'Controles físicos',          en: 'Physical controls' },
  '8-Technological':  { es: 'Controles tecnológicos',     en: 'Technological controls' },
};

const CATEGORY_ORDER = ['5-Organizational', '6-People', '7-Physical', '8-Technological'];

// ── SOA IMPORT COLUMNS ────────────────────────────────────────────────────────
const SOA_IMPORT_COLUMNS = [
  { key: 'controlId',                   label: 'controlId',                   type: 'string',  required: true },
  { key: 'applicable',                  label: 'applicable',                  type: 'boolean', required: false, default: null },
  { key: 'implementationStatus',        label: 'implementationStatus',        type: 'string',  required: false, default: null, enum: ['Not implemented', 'Partially implemented', 'Implemented'], caseInsensitive: true },
  { key: 'implementationNotes',         label: 'implementationNotes',         type: 'string',  required: false, default: '' },
  { key: 'justificationForInclusion',   label: 'justificationForInclusion',   type: 'string',  required: false, default: '' },
  { key: 'justificationForExclusion',   label: 'justificationForExclusion',   type: 'string',  required: false, default: '' },
  { key: 'owner',                       label: 'owner',                       type: 'string',  required: false, default: '' },
];

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:              'Declaración de aplicabilidad',
    versionLabel:       'Versión',
    approvedByLabel:          'Aprobado por',
    approvalDateLabel:        'Fecha de aprobación',
    approvalEvidenceIdLabel:  'ID evidencia de aprobación',
    save:               'Guardar',
    saving:             'Guardando…',
    saveOk:             '✓ Guardado',
    saveError:          'Error al guardar',
    unsavedLabel:        'Cambios sin guardar',
    unsavedConfirm:      'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving:  'Salir sin guardar',
    saveAndLeave:        'Guardar y salir',
    formCancel:          'Cancelar',
    cntApplicable:      'Aplicables',
    cntNotApplicable:   'No aplicables',
    cntImplemented:     'Implementados',
    cntPartial:         'Implementación parcial',
    colId:              'ID',
    colTitle:           'Control',
    colApplicable:      'Aplicable',
    colStatus:          'Estado',
    colJustification:   'Justificación',
    colNotes:           'Notas',
    yes:                'Sí',
    no:                 'No',
    justificationForInclusion: 'Justificación de inclusión',
    justificationForExclusion: 'Justificación de exclusión',
    notesPlaceholder:   'Notas de implementación',
    loading:            'Cargando…',
    applicable:         'aplicable',
    of:                 'de',
    implementationLabels: {
      'Not implemented':       'No implementado',
      'Partially implemented': 'Parcialmente implementado',
      'Implemented':           'Implementado',
    },
    downloadTemplate: 'Descargar plantilla',
    importBtn:       'Importar CSV',
    importTitle:     'Importar datos del SoA',
    importWarning:   'Aviso: los valores importados sobreescribirán los datos existentes de los controles que hagan match.',
    importUnknownId: 'Control ID no reconocido — fila ignorada.',
  },
  en: {
    title:              'Statement of Applicability',
    versionLabel:       'Version',
    approvedByLabel:          'Approved by',
    approvalDateLabel:        'Approval date',
    approvalEvidenceIdLabel:  'Approval evidence ID',
    save:               'Save',
    saving:             'Saving…',
    saveOk:             '✓ Saved',
    saveError:          'Save error',
    unsavedLabel:        'Unsaved changes',
    unsavedConfirm:      'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving:  'Leave without saving',
    saveAndLeave:        'Save and leave',
    formCancel:          'Cancel',
    cntApplicable:      'Applicable',
    cntNotApplicable:   'Not applicable',
    cntImplemented:     'Implemented',
    cntPartial:         'Partially implemented',
    colId:              'ID',
    colTitle:           'Control',
    colApplicable:      'Applicable',
    colStatus:          'Status',
    colJustification:   'Justification',
    colNotes:           'Notes',
    yes:                'Yes',
    no:                 'No',
    justificationForInclusion: 'Justification for inclusion',
    justificationForExclusion: 'Justification for exclusion',
    notesPlaceholder:   'Implementation notes',
    loading:            'Loading…',
    applicable:         'applicable',
    of:                 'of',
    implementationLabels: {
      'Not implemented':       'Not implemented',
      'Partially implemented': 'Partially implemented',
      'Implemented':           'Implemented',
    },
    downloadTemplate: 'Download template',
    importBtn:       'Import CSV',
    importTitle:     'Import SoA data',
    importWarning:   'Warning: imported values will overwrite existing data for matched controls.',
    importUnknownId: 'Unrecognized control ID — row skipped.',
  },
};

// ── SOA TEMPLATE DOWNLOAD ─────────────────────────────────────────────────────
function generateSoATemplate() {
  const headers = 'controlId,control,applicable,implementationStatus,implementationNotes,justificationForInclusion,justificationForExclusion,owner';
  const rows = SOA_CONTROLS.map((c) => `${c.id},"${c.title}",,,,,,`).join('\n');
  const csv = '\uFEFF' + headers + '\n' + rows + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'import-soa.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ── BUILD DEFAULT SOA DATA ────────────────────────────────────────────────────
function buildDefaultSoA(orgId) {
  const now = new Date().toISOString();
  return {
    orgId,
    version: '1.0',
    approvedBy: null,
    approvalDate: null,
    approvalEvidenceId: null,
    updatedAt: now,
    controls: SOA_CONTROLS.map((c) => ({
      id: c.id,
      category: c.category,
      title: c.title,
      applicable: true,
      justificationForInclusion: null,
      justificationForExclusion: null,
      implementationStatus: 'Not implemented',
      implementationNotes: '',
      owner: '',
      reviewDate: null,
      updatedAt: now,
    })),
  };
}

// ── SUMMARY CARD ──────────────────────────────────────────────────────────────
function SummaryCard({ value, label, color }) {
  return (
    <div style={{
      background: BG_SURFACE,
      border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 10,
      padding: '0.9rem 1.25rem',
    }}>
      <div style={{
        ...FONT_SMALL,
        color: TEXT_DEFAULT,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        marginBottom: '0.35rem',
      }}>
        {label}
      </div>
      <div style={{
        ...FONT_STAT_VALUE,
        color,
      }}>
        {value}
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function SoAView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;
  const [soaData, setSoaData] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'ok' | 'error'
  const [dirty, setDirty] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [showImport, setShowImport] = useState(false);
  // A.5.5 indicator pill — count and most recent updatedAt across Active Authorities.
  const [authoritiesCount, setAuthoritiesCount] = useState(0);
  const [authoritiesLastUpdated, setAuthoritiesLastUpdated] = useState(null);
  // A.5.6 indicator pill — count and most recent updatedAt across Active Special interest groups.
  const [sigsCount, setSigsCount] = useState(0);
  const [sigsLastUpdated, setSigsLastUpdated] = useState(null);
  const savedControlsRef = useRef(null);

  // ── Load authorities aggregate for the A.5.5 indicator pill ───────────────
  useEffect(() => {
    let cancelled = false;
    listOrgCollection(org.id, 'authorities')
      .then((items) => {
        if (cancelled) return;
        const active = (items || []).filter((a) => a && a.status === 'Active');
        let maxUpdated = null;
        for (const a of active) {
          if (a.updatedAt && (!maxUpdated || a.updatedAt > maxUpdated)) maxUpdated = a.updatedAt;
        }
        setAuthoritiesCount(active.length);
        setAuthoritiesLastUpdated(maxUpdated);
      })
      .catch(() => {
        if (cancelled) return;
        setAuthoritiesCount(0);
        setAuthoritiesLastUpdated(null);
      });
    return () => { cancelled = true; };
  }, [org.id]);

  // ── Load special interest groups aggregate for the A.5.6 indicator pill ──
  useEffect(() => {
    let cancelled = false;
    listOrgCollection(org.id, 'special-interest-groups')
      .then((items) => {
        if (cancelled) return;
        const active = (items || []).filter((s) => s && s.status === 'Active');
        let maxUpdated = null;
        for (const s of active) {
          if (s.updatedAt && (!maxUpdated || s.updatedAt > maxUpdated)) maxUpdated = s.updatedAt;
        }
        setSigsCount(active.length);
        setSigsLastUpdated(maxUpdated);
      })
      .catch(() => {
        if (cancelled) return;
        setSigsCount(0);
        setSigsLastUpdated(null);
      });
    return () => { cancelled = true; };
  }, [org.id]);

  // ── Load or initialize ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await readOrgFile(org.id, 'controls/soa.json');
        const migrated = {
          ...data,
          approvalEvidenceId: data.approvalEvidenceId ?? null,
          controls: (data.controls || []).map((cc) => {
            const next = {
              ...cc,
              justificationForInclusion:        cc.justificationForInclusion ?? cc.justification ?? null,
              justificationForExclusion:        cc.justificationForExclusion ?? null,
            };
            // Silent cleanup of removed reverse-link fields and the retired
            // per-control effectiveness/evidence fields: drop from in-memory
            // state so the next save persists a clean JSON. Source of truth for
            // the reverse links is now Risk.linkedControlIds and
            // Document.linkedControlIds (edited from their own forms).
            delete next.linkedRiskIds;
            delete next.linkedDocumentIds;
            delete next.evidenceIds;
            delete next.effectivenessRating;
            delete next.implementationDate;
            delete next.lastEffectivenessAssessmentDate;
            return next;
          }),
        };
        if (!cancelled) { setSoaData(migrated); savedControlsRef.current = migrated.controls; }
      } catch {
        const defaultData = buildDefaultSoA(org.id);
        try { await writeOrgFile(org.id, 'controls/soa.json', defaultData); } catch { /* best-effort */ }
        if (!cancelled) { setSoaData(defaultData); savedControlsRef.current = defaultData.controls; }
      }
    })();
    return () => { cancelled = true; };
  }, [org.id]);

  // ── Warn on browser refresh/close when dirty ─────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Update a single control field ─────────────────────────────────────────
  const handleControlChange = (id, field, value) => {
    setDirty(true);
    setSoaData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        controls: prev.controls.map((c) =>
          c.id === id ? { ...c, [field]: value, updatedAt: new Date().toISOString() } : c
        ),
      };
    });
  };

  // ── Update header field ───────────────────────────────────────────────────
  const handleHeaderChange = (field, value) => {
    setDirty(true);
    setSoaData((prev) => {
      if (!prev) return prev;
      if (field === 'version') return { ...prev, version: value };
      return { ...prev, [field]: value || null };
    });
  };

  // ── Bulk import: apply CSV rows to controls in memory ────────────────────
  const handleSoAImport = (rows) => {
    const VALID_STATUSES = ['Not implemented', 'Partially implemented', 'Implemented'];
    setSoaData((prev) => {
      if (!prev) return prev;
      const now = new Date().toISOString();
      return {
        ...prev,
        controls: prev.controls.map((c) => {
          const row = rows.find((r) => r.controlId === c.id);
          if (!row) return c;
          const updated = { ...c, updatedAt: now };
          if (row.applicable !== null && row.applicable !== undefined) {
            updated.applicable = row.applicable;
          }
          if (row.implementationStatus !== null && row.implementationStatus !== undefined && row.implementationStatus !== '' && VALID_STATUSES.includes(row.implementationStatus)) {
            updated.implementationStatus = row.implementationStatus;
          }
          if (row.implementationNotes) updated.implementationNotes = row.implementationNotes;
          if (row.justificationForInclusion) updated.justificationForInclusion = row.justificationForInclusion;
          if (row.justificationForExclusion) updated.justificationForExclusion = row.justificationForExclusion;
          if (row.owner) updated.owner = row.owner;
          return updated;
        }),
      };
    });
    setDirty(true);
    setShowImport(false);
  };

  // ── Save to file ──────────────────────────────────────────────────────────
  // Returns true on successful write, false on save error. The user-facing
  // Save button calls handleSave (no return value consumed). The App-level
  // guard relies on the boolean via the saveRequestTick / onSaveResult
  // contract (see GRC_UI_PATTERNS.md §5).
  const doSave = async () => {
    if (!soaData || saveState === 'saving') return false;
    setSaveState('saving');
    try {
      const changedBy = org.consultantPersonId || 'system';
      const updated = { ...soaData, updatedAt: new Date().toISOString() };
      updated.controls = appendControlChangeLogs(savedControlsRef.current, updated.controls, changedBy);
      await writeOrgFile(org.id, 'controls/soa.json', updated);
      savedControlsRef.current = updated.controls;
      setSoaData(updated);
      setDirty(false);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
      return true;
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
      return false;
    }
  };

  const handleSave = () => { doSave(); };

  // ── App-level guard: save on tick increase, report result back ───────────
  const lastProcessedTickRef = useRef(0);
  useEffect(() => {
    if (typeof saveRequestTick !== 'number') return;
    if (saveRequestTick <= lastProcessedTickRef.current) return;
    lastProcessedTickRef.current = saveRequestTick;
    (async () => {
      const ok = await doSave();
      onSaveResult?.(ok);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Toggle section collapse ───────────────────────────────────────────────
  const toggleCategory = (cat) => {
    setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (!soaData) {
    return (
      <div style={{
        ...FONT_LABEL,
        padding: '2rem',
        color: TEXT_MUTED,
      }}>
        {t.loading}
      </div>
    );
  }

  // ── Computed counters ─────────────────────────────────────────────────────
  const controls = soaData.controls || [];
  const cntApplicable    = controls.filter((c) => c.applicable).length;
  const cntNotApplicable = controls.filter((c) => !c.applicable).length;
  const cntImplemented   = controls.filter((c) => c.applicable && c.implementationStatus === 'Implemented').length;
  const cntPartial       = controls.filter((c) => c.applicable && c.implementationStatus === 'Partially implemented').length;

  // ── SOA_CONTROLS lookup map for i18n titles ───────────────────────────────
  const controlTitleMap = Object.fromEntries(SOA_CONTROLS.map(c => [c.id, c]));

  // ── Group controls by category ────────────────────────────────────────────
  const grouped = {};
  CATEGORY_ORDER.forEach((cat) => { grouped[cat] = []; });
  controls.forEach((c) => { if (grouped[c.category]) grouped[c.category].push(c); });

  // ── Shared table header cell style ────────────────────────────────────────
  const thStyle = {
    ...FONT_TINY,
    color: TEXT_MUTED,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    padding: '0.55rem 0.75rem',
    textAlign: 'left',
    borderBottom: `1px solid ${BORDER_DEFAULT}`,
    whiteSpace: 'nowrap',
    background: BG_SUBTLE,
  };

  const saveLabel = { idle: t.save, saving: t.saving, ok: t.saveOk, error: t.save }[saveState];
  const saveBg    = { idle: BRAND_PRIMARY, saving: TEXT_MUTED, ok: BRAND_PRIMARY_DARK, error: BRAND_PRIMARY }[saveState];
  const isSaving  = saveState === 'saving';

  const errorBannerSt = {
    ...FONT_LABEL,
    background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
    borderRadius: 6, padding: '0.35rem 0.75rem',
    marginBottom: '1rem',
  };

  return (
    <div>

      {/* ── Header bar ───────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '0.75rem',
      }}>
        {/* Left: title + meta fields */}
        <div>
          <h1 style={{
            ...FONT_PAGE_TITLE,
            color: TEXT_HEADING, margin: '0 0 0.6rem',
          }}>
            {t.title}
          </h1>
          {/* Inline editable meta fields */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
            {/* Version */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t.versionLabel}
              </span>
              <input
                value={soaData.version || ''}
                onChange={(e) => handleHeaderChange('version', e.target.value)}
                style={{
                  ...FONT_LABEL,
                  color: TEXT_DEFAULT,
                  border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 4, padding: '0.15rem 0.4rem',
                  background: BG_SURFACE, width: '5ch', outline: 'none',
                }}
              />
            </div>
            {/* Approved by */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t.approvedByLabel}
              </span>
              <div style={{ minWidth: 200 }}>
                <PeopleSelector orgId={org.id} lang={lang} value={soaData.approvedBy || ''} onChange={val => handleHeaderChange('approvedBy', val)} />
              </div>
            </div>
            {/* Approval date */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t.approvalDateLabel}
              </span>
              <input
                type="date"
                value={soaData.approvalDate || ''}
                onChange={(e) => handleHeaderChange('approvalDate', e.target.value)}
                style={{
                  ...FONT_LABEL,
                  color: TEXT_DEFAULT,
                  border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 4, padding: '0.15rem 0.4rem',
                  background: BG_SURFACE, outline: 'none',
                }}
              />
            </div>
          </div>
          {/* Approval evidence */}
          <div style={{ marginTop: '1rem' }}>
            <span style={{ display: 'block', ...FONT_TINY, color: TEXT_DISABLED, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>
              {t.approvalEvidenceIdLabel}
            </span>
            <EvidenceSelector
              orgId={org.id}
              lang={lang}
              value={soaData.approvalEvidenceId}
              onChange={(v) => handleHeaderChange('approvalEvidenceId', v)}
            />
          </div>
        </div>

      </div>

      {saveState === 'error' && (
        <div style={errorBannerSt}>{t.saveError}</div>
      )}

      <fieldset disabled={isSaving || showImport} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ── Summary bar ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0.75rem',
        marginBottom: '1.75rem',
      }}>
        <SummaryCard value={cntApplicable}    label={t.cntApplicable}    color={TEXT_HEADING} />
        <SummaryCard value={cntNotApplicable} label={t.cntNotApplicable} color={TEXT_MUTED} />
        <SummaryCard value={cntImplemented}   label={t.cntImplemented}   color={BRAND_PRIMARY_DARK} />
        <SummaryCard value={cntPartial}       label={t.cntPartial}       color={STATUS_WARN.text} />
      </div>

      {/* ── Category sections ─────────────────────────────────────────────── */}
      {CATEGORY_ORDER.map((cat) => {
        const catControls  = grouped[cat] || [];
        const catApplicable = catControls.filter((c) => c.applicable).length;
        const isCollapsed  = !!collapsed[cat];
        const catLabel     = CATEGORY_LABELS[cat];

        return (
          <div key={cat} style={{ marginBottom: '1.25rem' }}>

            {/* Section header button */}
            <button
              onClick={() => toggleCategory(cat)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: BG_SUBTLE,
                border: `1px solid ${BORDER_DEFAULT}`,
                borderRadius: isCollapsed ? 8 : '8px 8px 0 0',
                padding: '0.7rem 1rem',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{
                  ...FONT_BODY_STRONG, fontWeight: 700,
                  color: TEXT_HEADING,
                }}>
                  {catLabel ? (catLabel[lang] || catLabel.en) : cat}
                </span>
                <span style={{
                  ...FONT_TINY,
                  color: TEXT_DISABLED, letterSpacing: '0.03em',
                }}>
                  {catApplicable} {t.applicable} / {catControls.length}
                </span>
              </div>
              <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>
                {isCollapsed ? '▸' : '▾'}
              </span>
            </button>

            {/* Section table */}
            {!isCollapsed && (
              <div style={{
                overflowX: 'auto',
                border: `1px solid ${BORDER_DEFAULT}`,
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: '3.5rem' }}>{t.colId}</th>
                      <th style={{ ...thStyle, minWidth: 112 }}>{t.colTitle}</th>
                      <th style={{ ...thStyle, width: '6rem', textAlign: 'center' }}>{t.colApplicable}</th>
                      <th style={{ ...thStyle, width: 'calc(10rem + 10px)' }}>{t.colStatus}</th>
                      <th style={{ ...thStyle, minWidth: 241 }}>{t.colJustification}</th>
                      <th style={{ ...thStyle, minWidth: 187 }}>{t.colNotes}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catControls.map((control) => {
                      const notApplicable = !control.applicable;
                      const rowBg         = notApplicable ? '#f5f5f0' : undefined;
                      const statusColors  = STATUS_COLORS[control.implementationStatus] || STATUS_COLORS['Not implemented'];
                      const tdBase        = {
                        padding: '0.55rem 0.75rem',
                        borderBottom: `1px solid ${BG_SHELL}`,
                        verticalAlign: 'middle',
                      };

                      return (
                        <Fragment key={control.id}>
                        <tr style={{ background: rowBg }}>

                          {/* ID */}
                          <td style={{
                            ...tdBase,
                            ...FONT_LABEL,
                            color: TEXT_MUTED,
                            whiteSpace: 'nowrap',
                          }}>
                            <div>{aId(control.id)}</div>
                            {control.id === '5.5' && authoritiesCount > 0 && (
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => onNavigate?.('authorities')}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate?.('authorities'); } }}
                                title={lang === 'es' ? 'Autoridades registradas — abrir el registro' : 'Authorities tracked — open the register'}
                                style={{
                                  ...FONT_TINY,
                                  display: 'inline-block',
                                  marginTop: '0.25rem',
                                  background: STATUS_INFO.bg,
                                  color: STATUS_INFO.text,
                                  borderRadius: 999,
                                  padding: '0.15rem 0.5rem',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {authoritiesCount} · {authoritiesLastUpdated ? authoritiesLastUpdated.slice(0, 10) : ''}
                              </div>
                            )}
                            {control.id === '5.6' && sigsCount > 0 && (
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => onNavigate?.('special-interest-groups')}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate?.('special-interest-groups'); } }}
                                title={lang === 'es' ? 'Grupos de interés registrados — abrir el registro' : 'Special interest groups tracked — open the register'}
                                style={{
                                  ...FONT_TINY,
                                  display: 'inline-block',
                                  marginTop: '0.25rem',
                                  background: STATUS_INFO.bg,
                                  color: STATUS_INFO.text,
                                  borderRadius: 999,
                                  padding: '0.15rem 0.5rem',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {sigsCount} · {sigsLastUpdated ? sigsLastUpdated.slice(0, 10) : ''}
                              </div>
                            )}
                          </td>

                          {/* Title */}
                          <td style={{
                            ...tdBase,
                            ...FONT_LIST_ITEM,
                            color: notApplicable ? TEXT_DISABLED : TEXT_DEFAULT,
                            lineHeight: 1.35,
                          }}>
                            {lang === 'es' ? (controlTitleMap[control.id]?.title_es || control.title) : control.title}
                          </td>

                          {/* Applicable toggle */}
                          <td style={{ ...tdBase, textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleControlChange(control.id, 'applicable', true)}
                                style={{
                                  ...FONT_TINY, fontWeight: 600,
                                  background: control.applicable ? BRAND_PRIMARY : BG_SUBTLE,
                                  color: control.applicable ? SURFACE_WHITE : TEXT_MUTED,
                                  border: `1px solid ${control.applicable ? BRAND_PRIMARY : BORDER_DEFAULT}`,
                                  borderRadius: 4,
                                  padding: '0.15rem 0.5rem',
                                  cursor: 'pointer',
                                }}
                              >
                                {t.yes}
                              </button>
                              <button
                                onClick={() => handleControlChange(control.id, 'applicable', false)}
                                style={{
                                  ...FONT_TINY, fontWeight: 600,
                                  background: notApplicable ? ERROR_TEXT : BG_SUBTLE,
                                  color: notApplicable ? SURFACE_WHITE : TEXT_MUTED,
                                  border: `1px solid ${notApplicable ? ERROR_TEXT : BORDER_DEFAULT}`,
                                  borderRadius: 4,
                                  padding: '0.15rem 0.5rem',
                                  cursor: 'pointer',
                                }}
                              >
                                {t.no}
                              </button>
                            </div>
                          </td>

                          {/* Implementation status */}
                          <td style={tdBase}>
                            <select
                              value={control.implementationStatus}
                              onChange={(e) => handleControlChange(control.id, 'implementationStatus', e.target.value)}
                              disabled={notApplicable}
                              style={{
                                ...FONT_SMALL,
                                border: `1px solid ${BORDER_DEFAULT}`,
                                borderRadius: 4,
                                padding: '0.2rem 0.35rem',
                                background: notApplicable ? '#ebebeb' : statusColors.bg,
                                color: notApplicable ? TEXT_DISABLED : statusColors.text,
                                cursor: notApplicable ? 'default' : 'pointer',
                                width: '100%',
                                outline: 'none',
                                opacity: notApplicable ? 0.5 : 1,
                              }}
                            >
                              {['Not implemented','Partially implemented','Implemented'].map(v => (
                                <option key={v} value={v}>{t.implementationLabels[v]}</option>
                              ))}
                            </select>
                          </td>

                          {/* Justification (textarea; which field depends on applicable) */}
                          <td style={tdBase}>
                            {control.applicable ? (
                              <textarea
                                value={control.justificationForInclusion || ''}
                                onChange={(e) => handleControlChange(control.id, 'justificationForInclusion', e.target.value)}
                                placeholder={t.justificationForInclusion}
                                rows={2}
                                style={{
                                  ...FONT_LIST_ITEM,
                                  width: '100%',
                                  border: `1px solid ${BORDER_DEFAULT}`,
                                  borderRadius: 4,
                                  padding: '0.2rem 0.4rem',
                                  background: BG_SURFACE,
                                  color: TEXT_DEFAULT,
                                  outline: 'none',
                                  boxSizing: 'border-box',
                                  resize: 'vertical',
                                  minHeight: '2.2rem',
                                }}
                              />
                            ) : (
                              <textarea
                                value={control.justificationForExclusion || ''}
                                onChange={(e) => handleControlChange(control.id, 'justificationForExclusion', e.target.value)}
                                placeholder={t.justificationForExclusion}
                                rows={2}
                                style={{
                                  ...FONT_LIST_ITEM,
                                  width: '100%',
                                  border: `1px solid ${BORDER_DEFAULT}`,
                                  borderRadius: 4,
                                  padding: '0.2rem 0.4rem',
                                  background: '#f5f5f0',
                                  color: TEXT_DEFAULT,
                                  outline: 'none',
                                  boxSizing: 'border-box',
                                  resize: 'vertical',
                                  minHeight: '2.2rem',
                                }}
                              />
                            )}
                          </td>

                          {/* Notes (disabled when not applicable) */}
                          <td style={tdBase}>
                            <textarea
                              value={control.implementationNotes || ''}
                              onChange={(e) => handleControlChange(control.id, 'implementationNotes', e.target.value)}
                              placeholder={notApplicable ? '' : t.notesPlaceholder}
                              disabled={notApplicable}
                              style={{
                                ...FONT_LIST_ITEM,
                                width: '100%',
                                border: `1px solid ${BORDER_DEFAULT}`,
                                borderRadius: 4,
                                padding: '0.2rem 0.4rem',
                                background: notApplicable ? '#ebebeb' : BG_SURFACE,
                                color: TEXT_DEFAULT,
                                outline: 'none',
                                boxSizing: 'border-box',
                                resize: 'vertical',
                                minHeight: '2.2rem',
                              }}
                            />
                          </td>
                        </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      </fieldset>
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || 'Confirm'}
        cancelLabel={t.formCancel}
        secondaryAction={confirmState?.secondaryAction}
      />

      {showImport && (
        <div style={{
          position: 'fixed',
          top: 0, right: 0, bottom: 0, left: 0,
          background: 'rgba(15,34,53,0.45)',
          zIndex: 200,
        }}>
          <div style={{
            position: 'absolute',
            top: 0, right: 0, bottom: 0, left: 0,
            overflowY: 'auto',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '2rem',
          }}>
            <div style={{ maxWidth: 860, width: '100%', background: BG_SURFACE, borderRadius: 10 }}>
              <div style={{
                ...FONT_SMALL,
                color: UNSAVED_AMBER,
                padding: '0.75rem',
                background: STATUS_WARN.bg,
                borderRadius: 6,
                margin: '1rem 1rem 0',
              }}>
                <span style={{ display: 'block' }}>{t.importWarning}</span>
                <span style={{ display: 'block', marginTop: '0.25rem' }}>{t.importUnknownId}</span>
              </div>
              <ImportWizard
                lang={lang}
                dedupeKey="controlId"
                onClose={() => setShowImport(false)}
                onImport={handleSoAImport}
                templateFilename="import-soa.csv"
                title={{ es: T.es.importTitle, en: T.en.importTitle }}
                columns={SOA_IMPORT_COLUMNS}
                onDownloadTemplate={generateSoATemplate}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sticky action bar */}
      <div style={{
        position: 'sticky', bottom: 0,
        marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '-2.5rem',
        marginTop: '1.5rem',
        padding: '0.85rem 1.5rem', background: BG_SURFACE,
        borderTop: `1px solid ${BORDER_DEFAULT}`, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          {dirty && (
            <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>
              {t.unsavedLabel}
            </span>
          )}
          <button
            onClick={() => setShowImport(true)}
            disabled={saveState === 'saving'}
            style={{
              ...BTN_SECONDARY,
              ...FONT_BUTTON,
              borderRadius: 8,
              padding: '0.55rem 1.25rem',
              cursor: saveState === 'saving' ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {t.importBtn}
          </button>
          <button
            onClick={handleSave}
            disabled={saveState === 'saving'}
            style={{
              ...FONT_BUTTON,
              background: saveBg, color: SURFACE_WHITE, border: 'none',
              borderRadius: 8, padding: '0.55rem 1.25rem',
              cursor: saveState === 'saving' ? 'default' : 'pointer',
              transition: 'background 0.2s', whiteSpace: 'nowrap',
            }}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
